import React, { useState, useEffect } from "react";
import FunctionCard from "../../components/PageComponents/Function/FunctionCard";
import classes from "../../assets/styles/Pages/Function/Function.module.css";
import HeroSection from "../../components/HeroSection";
import FunctionCreateCardsData from "../../utils/data/FunctionCreateCardsData";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  HOMEDASHBOARD,
  PRIORITY,
  TEAMDETAIL,
  TOGGLE_TEAM_FUNCTION_VISIBILITY,
} from "../../utils/constants/apiUrl";
import { get, post, put } from "../../server";
import { loggedInUser, teamIDCheck } from "../../utils/store";
import { useAtom } from "jotai";
import showToast from "../../helpers/showToast";
import { RiListUnordered } from "react-icons/ri";
import useFunctionsCount from "../../hooks/useFunctionsCount";
import PriorityListingModal from "../../components/Modals/PriorityListingModal";
import { Button, Modal } from "react-bootstrap";

const Function = () => {
  const { teamId, userId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const memberAdminFromUrl = searchParams.get("member_admin") === "1";
  const [loggedUser] = useAtom(loggedInUser);
  const [userTeamId, setUserTeamId] = useAtom(teamIDCheck);
  const [loading, setLoading] = useState(true);
  const [personalCardsCount, setPersonalCardsCount] = useState(0);
  const [businessCardsCount, setBusinessCardsCount] = useState(0);
  const [formBuilderCount, setFormBuilderCount] = useState(0);
  const [memoryCount, setMemoryCount] = useState(0);
  const [medicalCount, setMedicalCount] = useState(0);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [customUrls, setCustomUrls] = useState([]);
  const [payment, setPayment] = useState([]);
  const [cashApps, setCashApps] = useState([]);
  const [venmos, setVenmos] = useState([]);
  const [files, setFiles] = useState([]);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [sortedFunctionData, setSortedFunctionData] = useState([]);
  const [tempSortedFunctionData, setTempSortedFunctionData] = React.useState(
    []
  );
  const [updatingMemberVisibility, setUpdatingMemberVisibility] = useState(null);
  const [selectedMemberIsAdmin, setSelectedMemberIsAdmin] = useState(
    memberAdminFromUrl || Boolean(location.state?.selectedMemberIsAdmin)
  );
  const isManagedMemberView = Boolean(teamId && userId);
  const canManageMemberVisibility =
    !isManagedMemberView &&
    Boolean(userTeamId) &&
    (loggedUser?.role?.includes("team_owner") ||
      loggedUser?.role?.includes("team_admin"));
  const isTeamMemberPanel =
    (isManagedMemberView && !selectedMemberIsAdmin) ||
    ((loggedUser?.connected_teams?.length ?? 0) > 0 &&
      !selectedMemberIsAdmin &&
      !loggedUser?.role?.includes("team_owner") &&
      !loggedUser?.role?.includes("team_admin"));
  const showAllFunctions =
    memberAdminFromUrl ||
    selectedMemberIsAdmin ||
    loggedUser?.role?.includes("team_admin");
  const readOnlyAdminView =
    isManagedMemberView && (memberAdminFromUrl || selectedMemberIsAdmin);

  useEffect(() => {
    let active = true;

    const loadSelectedMemberRole = async () => {
      const roleTeamId = teamId || userTeamId;
      const roleUserId = userId || loggedUser?.id;

      if (!roleTeamId || !roleUserId) {
        setSelectedMemberIsAdmin(false);
        return;
      }

      const response = await get(
        `${TEAMDETAIL}/${roleTeamId}`,
        loggedUser?.token || null
      );
      if (!active) return;
      const members = response?.status
        ? response.data?.team_members || response.data?.team?.team_members || []
        : [];
      const member = members.find(
        (item) =>
          String(item.user_id ?? item.user_data?.id ?? item.id) ===
          String(roleUserId)
      );
      if (member) {
        const memberRoles = member.role || member.user_data?.role || [];
        const apiSaysMemberIsAdmin =
          member.is_admin === true ||
          Number(member.is_admin) === 1 ||
          member.isAdmin === true ||
          Number(member.isAdmin) === 1 ||
          member.is_team_admin === true ||
          Number(member.is_team_admin) === 1 ||
          member.user_data?.is_admin === true ||
          Number(member.user_data?.is_admin) === 1 ||
          memberRoles.includes?.("team_admin");
        // A confirmed admin value passed from the Team Members row must not be
        // downgraded by a differently shaped/stale team-detail response.
        setSelectedMemberIsAdmin(
          (currentValue) => currentValue || apiSaysMemberIsAdmin
        );
      }
    };

    loadSelectedMemberRole();
    return () => {
      active = false;
    };
  }, [loggedUser?.id, loggedUser?.token, teamId, userId, userTeamId]);

  const handleMemberVisibilityChange = async (functionType, isVisible) => {
    if (!canManageMemberVisibility || updatingMemberVisibility) return;
    setUpdatingMemberVisibility(functionType);
    const response = await put(
      TOGGLE_TEAM_FUNCTION_VISIBILITY,
      {
        team_id: Number(userTeamId),
        function_type: functionType,
        is_member_visible: isVisible ? 1 : 0,
      },
      loggedUser?.token || null
    );
    if (response?.status) {
      const updateSetting = (items) =>
        items.map((item) =>
          item.function_type === functionType
            ? { ...item, is_member_visible: isVisible }
            : item
        );
      setSortedFunctionData(updateSetting);
      setTempSortedFunctionData(updateSetting);
    } else {
      showToast(false, response.message || "Member visibility could not be updated.");
    }
    setUpdatingMemberVisibility(null);
  };

  const getHomeDashboardData = async () => {
    let queryParams = "";

    if (userTeamId) {
      queryParams = `?team_id=${userTeamId}`;
    }
    let { status, data, message } = await get(
      `${HOMEDASHBOARD}${queryParams}`,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );

    if (status) {
      if (Object.keys(data).length > 0) {
        setPersonalCardsCount(data.personalCardCount);
        setBusinessCardsCount(data.businessCardCount);
        setCustomUrls(data.customUrls);
        setEmergencyContacts(data.emergencyContacts);
        setPayment(data.onlyPayments);
        setFiles(data.fileUploads);
        setFormBuilderCount(data.formBuilderCount);
        setMemoryCount(data.memoryCount);
        setMedicalCount(data.medicalIdCount);
      }
    }
  };

  // const handlePriorityModalShow = () => {
  //   setShowPriorityModal(true);
  // };

  // const handlePriorityModalClose = () => {
  //   setShowPriorityModal(false);
  // };
  const handlePriorityModalShow = () => {
    // Deep copy to isolate temporary state
    const modalData = showAllFunctions
      ? FunctionCreateCardsData.map((card) => {
          const savedCard = sortedFunctionData.find(
            (item) => item.function_type === card.function_type
          );
          return {
            ...card,
            ...(savedCard || {}),
            is_member_visible: true,
            is_visible: true,
          };
        })
      : sortedFunctionData;
    setTempSortedFunctionData(JSON.parse(JSON.stringify(modalData)));
    setShowPriorityModal(true);
  };

  const handlePriorityModalClose = () => {
    setTempSortedFunctionData([]); // Reset temporary state
    setShowPriorityModal(false);
  };
  // const handlePriorityChange = (index, newValue) => {
  //   const updatedSortedFunctionData = [...sortedFunctionData];
  //   updatedSortedFunctionData[index].priority = newValue;
  //   setSortedFunctionData(updatedSortedFunctionData);
  // };

  // const handlePriorityChange = (index, newValue, field = "priority") => {
  //   const updatedSortedFunctionData = [...sortedFunctionData];
  //   if (field === "priority") {
  //     updatedSortedFunctionData[index].priority = newValue;
  //   } else if (field === "is_visible") {
  //     updatedSortedFunctionData[index].is_visible = newValue;
  //   }
  //   setSortedFunctionData(updatedSortedFunctionData);
  // };
  const handlePriorityChange = (index, newValue, field = "priority") => {
    const updatedTempData = [...tempSortedFunctionData];
    if (field === "priority") {
      updatedTempData[index].priority = newValue;
    } else if (field === "is_visible") {
      updatedTempData[index].is_visible = newValue;
    }
    setTempSortedFunctionData(updatedTempData); // Update temporary state
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (readOnlyAdminView) {
      showToast(
        true,
        "This team member is an admin and can access all function cards. No priority or visibility changes were saved."
      );
      return;
    }
    setLoading(true);
    const effectiveTeamId = teamId || userTeamId;
    const targetUserId = userId ? Number(userId) : null;
    const userFunctionSettings = tempSortedFunctionData.map((item) => ({
      function_type: item.function_type,
      priority: item.priority || 0, // Ensure priority is not empty
      is_visible: item.is_visible ? 1 : 0,
      ...(targetUserId
        ? { user_id: targetUserId, team_id: Number(effectiveTeamId) }
        : {}),
    }));
    // const userFunctionSettings = [];
    // const functionRows = document.querySelectorAll(".function-row");
    // functionRows.forEach((row) => {
    //   // const functionType = row.querySelector(
    //   //   'select[name="function_type"]'
    //   // ).value;
    //   const functionType = row.querySelector(
    //     'input[name="function_type"]'
    //   ).value;
    //   const priority = row.querySelector('input[name="priority"]').value;
    //   const isVisible = row.querySelector('input[type="checkbox"]').checked
    //     ? 1
    //     : 0;
    //   userFunctionSettings.push({
    //     function_type: functionType,
    //     priority: priority,
    //     is_visible: isVisible,
    //   });
    // });
    // const payload = {
    //   team_id: teamId,
    //   userFunctionSettings: userFunctionSettings,
    // };
    const payload = {
      team_id: effectiveTeamId ? Number(effectiveTeamId) : null,
      ...(targetUserId ? { user_id: targetUserId } : {}),
      userFunctionSettings,
    };

    const saveUrl = targetUserId
      ? `${PRIORITY}?team_id=${Number(effectiveTeamId)}&user_id=${targetUserId}`
      : PRIORITY;

    // Make API call
    let { status, message, data } = await post(
      saveUrl,
      payload,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );

    // if (status) {
    //   priorityFunction();
    //   setShowPriorityModal(false);
    // } else {
    //   showToast(status, message);
    // }
    if (status) {
      setSortedFunctionData([...tempSortedFunctionData]);
      setShowPriorityModal(false);
      priorityFunction();
    } else {
      showToast(status, message);
    }

    setLoading(false);
  };

  const priorityFunction = async () => {
    setLoading(true);
    const effectiveTeamId = teamId || userTeamId;
    const queryParams = new URLSearchParams();
    if (effectiveTeamId) queryParams.set("team_id", effectiveTeamId);
    if (userId) queryParams.set("user_id", userId);
    const queryString = queryParams.toString();
    let { status, message, data } = await get(
      queryString ? `${PRIORITY}?${queryString}` : PRIORITY,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );
    if (status) {
      priorityData(data);
    } else {
      showToast(status, message);
    }
    setLoading(false);
  };

  const priorityData = (sortPriorityData) => {
    const sortedData = [...FunctionCreateCardsData].map((card) => {
      const priorityItem = sortPriorityData.find(
        (item) => item.function_type === card.function_type
      );
      return {
        ...card,
        priority: priorityItem ? priorityItem.priority : 0,
        is_member_visible: priorityItem
          ? Number(priorityItem.is_member_visible) === 1
          : true,
        // New members inherit the owner's allowed functions until they save
        // their own Visible preferences.
        is_visible: priorityItem ? Number(priorityItem.is_visible) === 1 : true,
      };
    });

    sortedData.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
    });

    setSortedFunctionData(sortedData);
  };
  useEffect(() => {
    getHomeDashboardData();
    priorityFunction();
  }, [teamId, userId, userTeamId]);

  const functionCardsToDisplay = showAllFunctions
    ? FunctionCreateCardsData.map((card) => {
        const savedCard = sortedFunctionData.find(
          (item) => item.function_type === card.function_type
        );
        return {
          ...card,
          ...(savedCard || {}),
          is_member_visible: true,
          is_visible: true,
        };
      })
    : sortedFunctionData;

  return (
    <>
      <div className="mt-3 heading">
        <h1>Functions</h1>
      </div>
      <div className="mainWrapper">
        <HeroSection />
      </div>
      <div className="col-12 text-end mt-3">
        <Button
          variant="outline-primary"
          className="priorityBtn"
          onClick={handlePriorityModalShow}
        >
          <RiListUnordered /> <span>PRIORITY FUNCTION</span>
        </Button>
      </div>
      <div className="boxWrapper p-0">
        {/* <button
          className="priorityBtn"
          onClick={handlePriorityModalShow}
        >
          <RiListUnordered /> <span>PRIORITY FUNCTION</span>
        </button> */}

        {/* {FunctionCreateCardsData.map((card) => {
          return (
            <FunctionCard
              key={card.id}
              {...card}
              teamId={teamId}
              userId={userId}
              personalCardsCount={personalCardsCount}
              businessCardsCount={businessCardsCount}
              customUrls={customUrls}
              emergencyContacts={emergencyContacts}
              payment={payment}
              files={files}
            />
          );
        })} */}
        {functionCardsToDisplay.map(
          (card) =>
            (showAllFunctions ||
              (card?.is_visible === true &&
                (!isTeamMemberPanel || card.is_member_visible === true))) && (
              <FunctionCard
                key={card.id}
                {...card}
                teamId={teamId}
                userId={userId}
                personalCardsCount={personalCardsCount}
                businessCardsCount={businessCardsCount}
                customUrls={customUrls}
                emergencyContacts={emergencyContacts}
                payment={payment}
                files={files}
                formBuilderCount={formBuilderCount}
                memoryCount={memoryCount}
                medicalCount={medicalCount}
                isVisible={card.is_visible ? " " : "cardHide"}
              />
            )
        )}
      </div>
      {/* <Modal
        centered
        className={`customModal priorityModal`}
        size="md"
        show={showPriorityModal}
      >
        <Modal.Header className={classes.priorityModalHeader}>
          <span>Function Priority List</span>
          <img
            src={crossicon}
            alt=""
            style={{ cursor: "pointer" }}
            onClick={handlePriorityModalClose}
          />
        </Modal.Header>

        <Modal.Body className={classes.priorityModalBody}>
          <div className={`${classes.imageUpload}`}>
            {sortedFunctionData.map((functionItem, index) => (
              <div
                className="row function-row align-items-center mb-3"
                key={index}
              >
                
                <div className="col-lg-2 d-none">
                  <FormInput
                    type="text"
                    name="function_type"
                    label="Function Name"
                    value={functionItem.function_type}
                    disabled
                  />
                </div>
                <div className="col-lg-9">
                  <FormInput
                    type="text"
                    name="priority"
                    label={`Enter Priority Number for ${functionItem.title}`}
                    value={
                      functionItem.priority === 0 ? "" : functionItem.priority
                    }
                    handleChange={(e) =>
                      handlePriorityChange(index, e.target.value)
                    }
                  />
                </div>
                <div className="col-lg-3">
                  <div className="form-check mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value=""
                      id={`isVisible${index}`}
                    />
                    <label
                      className="form-check-label mb-0"
                      htmlFor={`isVisible${index}`}
                    >
                      Visible
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <button
              className={`${classes.saveBtn} mb-3 mt-0`}
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </Modal.Body>
      </Modal> */}
      {/* <PriorityListingModal
        show={showPriorityModal}
        sortedFunctionData={sortedFunctionData}
        handlePriorityChange={handlePriorityChange}
        handleSave={handleSave}
        handleClose={handlePriorityModalClose}
      /> */}
      <PriorityListingModal
        show={showPriorityModal}
        sortedFunctionData={tempSortedFunctionData} // Use temporary state
        handlePriorityChange={handlePriorityChange}
        handleSave={handleSave}
        handleClose={handlePriorityModalClose}
        respectMemberVisibility={isTeamMemberPanel}
        canManageMemberVisibility={canManageMemberVisibility}
        handleMemberVisibilityChange={handleMemberVisibilityChange}
        updatingMemberVisibility={updatingMemberVisibility}
        readOnlyAdminView={readOnlyAdminView}
      />
    </>
  );
};

export default Function;
